import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { SmartInsightDay } from 'lumen/models';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  COMPARETYPES,
  RATE_PARAMS,
} from 'lumen/utils/constants/general-constants';
import {
  ACTIVE_FILTERS,
  DETAIL_PANE_DETAIL_SECTION_CARD_IDS,
} from 'lumen/utils/constants/market-insight-constants';
import { OD_SELECTED_TILES_IDS } from 'lumen/utils/constants/overview-dashboard-constants';
import { RATES_VIEWS } from 'lumen/utils/constants/rate-constants';
import { KPIS } from 'lumen/utils/constants/ri-constant-values';
import { stringifyObject } from 'lumen/utils/parse-utils';
import { DAY_BY_DAY_PATHS } from 'lumen/utils/spider';

export type SmartInsightDayInsightEntrySource = {
  hasAccess: boolean;
  href: string;
  navigateToSource: (targetBlank?: boolean) => void;
  productName: string;
};

export const SMART_INSIGHT_DAY_ENTRY_TYPE = {
  ADR: 'adr',
  EVENTS: 'events',
  EVENTS_NEW: 'events_new',
  OCCUPANCY: 'occupancy',
  PICKUP: 'pickup',
  PRICE_POSITION: 'price_position',
  RATES: 'rates',
  REVPAR: 'revpar',
  SELLOUT: 'sellout',
  DEMAND: 'demand',
  LOS_RESTRICTIONS: 'los_restrictions',
  HOLIDAYS: 'holidays',
} as const;
export type SmartInsightEntryType =
  (typeof SMART_INSIGHT_DAY_ENTRY_TYPE)[keyof typeof SMART_INSIGHT_DAY_ENTRY_TYPE];

const SMART_INSIGHT_DAY_ENTRY_ICONS: Record<SmartInsightEntryType, IconName> = {
  [SMART_INSIGHT_DAY_ENTRY_TYPE.ADR]: 'banknotes',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS]: 'view-calendar',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS_NEW]: 'calendar-add',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.OCCUPANCY]: 'meals-room-only',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.PICKUP]: 'meals-room-only',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.PRICE_POSITION]: 'rates-lowest',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.RATES]: 'banknotes',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.REVPAR]: 'banknotes',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.SELLOUT]: 'meals-room-only',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.DEMAND]: 'view-chart',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.LOS_RESTRICTIONS]: 'calendar-clock',
  [SMART_INSIGHT_DAY_ENTRY_TYPE.HOLIDAYS]: 'events-holiday',
};

const INSIGHT_TYPE_TO_OVERVIEW_TILE_ID: Record<SmartInsightEntryType, string> =
  {
    [SMART_INSIGHT_DAY_ENTRY_TYPE.ADR]: OD_SELECTED_TILES_IDS.my_adr,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS]: OD_SELECTED_TILES_IDS.events,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS_NEW]: OD_SELECTED_TILES_IDS.events,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.OCCUPANCY]: OD_SELECTED_TILES_IDS.occupancy,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.PICKUP]: OD_SELECTED_TILES_IDS.occupancy,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.PRICE_POSITION]:
      OD_SELECTED_TILES_IDS.pricing_difference,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.RATES]:
      OD_SELECTED_TILES_IDS.pricing_difference,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.REVPAR]: OD_SELECTED_TILES_IDS.my_revenue,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.SELLOUT]:
      OD_SELECTED_TILES_IDS.pricing_difference,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.DEMAND]: OD_SELECTED_TILES_IDS.demand_level,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.LOS_RESTRICTIONS]:
      OD_SELECTED_TILES_IDS.pricing_difference,
    [SMART_INSIGHT_DAY_ENTRY_TYPE.HOLIDAYS]: OD_SELECTED_TILES_IDS.events,
  };

const TEXT_VARIABLES_RATES = {
  OWN_VS_MEDIAN_PRCT: 'own_vs_median_prct',
  OWN_VS_COMP_PRCT: 'own_vs_comp_prct',
  REFERENCE_PRCT_STLY: 'reference_prct_stly',
  NB_ANOMALOUS_COMPETITORS: 'nb_anomalous_competitors',
  TOTAL_COMPETITOR_ANOMALIES: 'total_competitor_anomalies',
  NB_OTHER_COMPETITORS: 'nb_other_competitors',
};

const TEXT_VARIABLES_PICKUP = {
  REF: 'ref',
};

const PICKUP_SEVEN_DAYS_AGO_TEMPLATE_KEYS = new Set([
  'INSIGHT_SEVEN_DAYS_AGO_BASE_LOSS_SEGMENT',
  'INSIGHT_SEVEN_DAYS_AGO_BASE_PICKUP_SEGMENT',
  'INSIGHT_SEVEN_DAYS_AGO_BASE_LOSS_NO_SEGMENT',
  'INSIGHT_SEVEN_DAYS_AGO_BASE_PICKUP_NO_SEGMENT',
  'INSIGHT_SEVEN_DAYS_AGO_LOSS_W_STLY_REFERENCE_SEGMENT',
  'INSIGHT_SEVEN_DAYS_AGO_PICKUP_W_STLY_REFERENCE_SEGMENT',
  'INSIGHT_SEVEN_DAYS_AGO_LOSS_W_STLY_REFERENCE_NO_SEGMENT',
  'INSIGHT_SEVEN_DAYS_AGO_PICKUP_W_STLY_REFERENCE_NO_SEGMENT',
]);

const TEXT_VARIABLES_SELLOUT = {
  CHANNEL_NAME: 'channel_name',
  REF_CHANNEL_NAME: 'ref_channel_name',
};

const TEXT_VARIABLES_HOLIDAYS = {
  HOLIDAY_NAMES: 'holiday_names',
  HOLIDAY_NAME: 'holiday_name',
  HOLIDAY_NAME_ONE: 'holiday_name_one',
  HOLIDAY_NAME_TWO: 'holiday_name_two',
};

const TEXT_VARIABLES_EVENTS = {
  EVENT_NAME: 'event_name',
  START_DATE: 'start_date',
  END_DATE: 'end_date',
};

const TEXT_VARIABLES_EVENTS_NEW = {
  EVENT_NAMES: 'event_names',
  EVENT_NAME: 'event_name',
  EVENT_NAME_ONE: 'event_name_one',
  EVENT_NAME_TWO: 'event_name_two',
};

const TEXT_VARIABLES_OCCUPANCY = {
  VALUE: 'value',
  VALUE_TRANSIENT: 'value_transient',
  VALUE_GROUP: 'value_group',
  VALUE_MARKET_REF: 'value_market_ref',
  VALUE_MARKET_REF_DIFF: 'value_market_ref_diff',
};

const TEXT_VARIABLES_ADR = {
  PER_DIFF: 'per_diff',
};

const TEXT_VARIABLES_REVPAR = {
  PER_DIFF: 'per_diff',
};

const CHANNEL_NAMES_MAPPING = {
  [OTA_IDS.BOOKINGDOTCOM]: 'Booking.com',
  [OTA_IDS.BRANDDOTCOM]: 'Brand.com',
};

export default class SmartInsightDayInsightEntryModel extends Model {
  [Type] = 'smart-insights/smart-insight-day-insight-entry' as const;

  @belongsTo('smart-insights/smart-insight-day', {
    async: false,
    inverse: 'insightEntries',
  })
  declare smartInsightDay: SmartInsightDay;

  @attr() declare config: {
    currency: SupportedCurrencies;
    extract_date: string;
    segment: string;
    subscription_id: string;
  };

  @attr('smart-insight-info') declare info: {
    connectedDates: string[];
    insightElementType: SmartInsightEntryType;
    relevance: number;
    stayDate: string;
    text: {
      template: string;
      templateKey: string;
      variables: Record<string, string | number | string[]>;
    };
  };

  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare spider: Services['spider'];

  get dateRange(): { from: dayjs.Dayjs | null; to: dayjs.Dayjs | null } {
    const connectedDates = this.info.connectedDates.map((dateString) =>
      dayjs(dateString),
    );

    return {
      from: dayjs.min(connectedDates),
      to: dayjs.max(connectedDates),
    };
  }

  get formattedDateRange(): string {
    if (this.dateRange.from?.isSame(this.dateRange.to)) {
      return this.dateRange.from?.format('ll');
    }

    return `${this.dateRange.from?.format('ll')} - ${this.dateRange.to?.format('ll')}`;
  }

  @cached
  get formattedText(): string {
    const { template } = this.info.text;

    // Get processed variables, keeping numbers as numbers for plural selection
    const processedVariables = this.processVariables();

    try {
      // Use intl.formatMessage to handle variable replacement and plural forms
      return this.intl.formatMessage(template, processedVariables);
    } catch (e) {
      this.errorTracking.captureMessage(
        'Smart Insights: insight template formatting failed',
        {
          templateKey: this.info.text.templateKey,
          insightElementType: this.info.insightElementType,
          stayDate: this.info.stayDate,
          template,
          processedVariables,
          error: e instanceof Error ? e.message : String(e),
        },
      );
      return '';
    }
  }

  private get hasEventsAccess(): boolean {
    return this.hasSmartInsightsFeature('app.events');
  }

  private get hasMarketInsightAccess(): boolean {
    return this.hasSmartInsightsFeature('app.market_insight');
  }

  private get hasOTBAccess(): boolean {
    return this.hasSmartInsightsFeature('pms.otb.view');
  }

  private get hasOverviewDashboardTabAccess(): boolean {
    return this.hasSmartInsightsFeature('overview.view');
  }

  private get hasRatesAccess(): boolean {
    return this.hasSmartInsightsFeature('app.rates');
  }

  private get hasRevenueInsightAccess(): boolean {
    return this.hasSmartInsightsFeature('app.revenue_insight');
  }

  private get hasSpiderAccess(): boolean {
    return this.hasSmartInsightsFeature('app.spider');
  }

  get iconName(): IconName {
    return SMART_INSIGHT_DAY_ENTRY_ICONS[this.info.insightElementType];
  }

  @cached
  private get possibleSources(): SmartInsightDayInsightEntrySource[] {
    const { insightElementType, stayDate } = this.info;
    const month = stayDate.slice(0, 7); // YYYY-MM format

    const sources: SmartInsightDayInsightEntrySource[] = [];

    switch (insightElementType) {
      case SMART_INSIGHT_DAY_ENTRY_TYPE.ADR:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.REVPAR: {
        // Revenue Insight applies customer-specific exclusion rules that may cause
        // data mismatches with Smart Insights. Prioritize Spider and Overview which
        // show data closer to what Smart Insights displays. (BENI-2869)
        sources.push(this.getSourceSpider());
        sources.push(this.getSourceOverview(stayDate));
        sources.push(this.getSourceBookings(stayDate));
        break;
      }
      case SMART_INSIGHT_DAY_ENTRY_TYPE.OCCUPANCY: {
        // Revenue Insight applies customer-specific exclusion rules that may cause
        // data mismatches with Smart Insights. Prioritize Spider, Rate Insight, and
        // Overview which show data closer to what Smart Insights displays. (BENI-2869)
        sources.push(this.getSourceSpider());
        if (this.hasOTBAccess) {
          sources.push(this.getSourceRateInsight(month));
        }
        sources.push(this.getSourceOverview(stayDate));
        sources.push(this.getSourceBookings(stayDate));
        break;
      }
      case SMART_INSIGHT_DAY_ENTRY_TYPE.PICKUP: {
        // Revenue Insight applies customer-specific exclusion rules that may cause
        // data mismatches with Smart Insights. Prioritize Spider and Rate Insight
        // which show data closer to what Smart Insights displays. (BENI-2869)
        sources.push(this.getSourceSpider());
        sources.push(this.getSourceRateInsight(month));
        sources.push(this.getSourceOverview(stayDate));
        sources.push(this.getSourceBookings(stayDate));
        break;
      }
      case SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS_NEW:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.HOLIDAYS: {
        sources.push(this.getSourceEvents(month));
        sources.push(this.getSourceOverview(stayDate));
        break;
      }
      case SMART_INSIGHT_DAY_ENTRY_TYPE.PRICE_POSITION:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.RATES:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.SELLOUT:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.LOS_RESTRICTIONS: {
        sources.push(this.getSourceRateInsight(month));
        sources.push(this.getSourceOverview(stayDate));
        break;
      }
      case SMART_INSIGHT_DAY_ENTRY_TYPE.DEMAND: {
        sources.push(this.getSourceMarketInsight(stayDate));
        sources.push(this.getSourceRateInsight(month));
        sources.push(this.getSourceOverview(stayDate));
        break;
      }
      default:
        break;
    }

    return sources;
  }

  get source(): SmartInsightDayInsightEntrySource | null {
    // Return the first source that the user has access to.
    return this.possibleSources.find((source) => source.hasAccess) || null;
  }

  private get subscriptionId(): string {
    return this.config.subscription_id;
  }

  private get unifiedHotelViewSubscriptionIds(): {
    biRedirectId: string;
    rateiRedirectId: string;
  } {
    const { smartInsightDay } = this;
    const { meta } = smartInsightDay;

    return {
      rateiRedirectId: meta.rateiRedirectId ?? this.subscriptionId,
      biRedirectId: meta.biRedirectId ?? this.subscriptionId,
    };
  }

  private getSourceBookings(
    stayDate: string,
  ): SmartInsightDayInsightEntrySource {
    const { insightElementType } = this.info;

    const defaultQueryParamsRevenueInsight: Record<string, unknown> = {
      ...DEFAULT_QP_VALUES.hotel['revenue-insight'],
    };

    // Remove query params not specific to the filters, so that the preferred view of the product stays the same for the user.
    // E.g. if they've last selected month aggregation, we should not switch to day aggregation.
    delete defaultQueryParamsRevenueInsight['stayDateAggregation'];
    delete defaultQueryParamsRevenueInsight['cancellationsView'];

    // Make sure that the KPI which the Smart Insight is talking about, is visible in the product when navigating to the product.
    switch (insightElementType) {
      case SMART_INSIGHT_DAY_ENTRY_TYPE.OCCUPANCY: {
        defaultQueryParamsRevenueInsight['roomsTab'] = KPIS.OCCUPANCY;
        break;
      }

      case SMART_INSIGHT_DAY_ENTRY_TYPE.PICKUP: {
        defaultQueryParamsRevenueInsight['roomsTab'] = KPIS.NR_OF_ROOMS;
        break;
      }

      case SMART_INSIGHT_DAY_ENTRY_TYPE.REVPAR: {
        defaultQueryParamsRevenueInsight['revenueTab'] = KPIS.REVPAR;
        break;
      }
    }

    const subscriptionToRedirectTo =
      this.unifiedHotelViewSubscriptionIds.biRedirectId;

    const queryParams = {
      ...defaultQueryParamsRevenueInsight,
      // Currently the Smart Insights are always based on the STLY reference, this might change in the future though.
      // Revenue Insight also has a different term for "STLY", they currently call it "SWLY".
      reference: 'SWLY',
      fromDate: stayDate,
      toDate: stayDate,
      smartInsightsDay: stayDate,
      highlight: `revenue-insight-kpi-block-${insightElementType}`,
    };
    const routeName = 'hotel.revenue-insight';
    const href = this.router.urlFor(routeName, subscriptionToRedirectTo, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.bookings'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, subscriptionToRedirectTo, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasRevenueInsightAccess,
    };
  }

  private getSourceEvents(month: string): SmartInsightDayInsightEntrySource {
    const subscriptionToRedirectTo = this.subscriptionId;

    const queryParams = {
      highlight: `smart-insight-day-${this.info.stayDate}`,
      month,
      smartInsightsDay: this.info.stayDate,
    };
    const routeName = 'hotel.events';
    const href = this.router.urlFor(routeName, subscriptionToRedirectTo, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.events'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, subscriptionToRedirectTo, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasEventsAccess,
    };
  }

  private getSourceMarketInsight(
    stayDate: string,
  ): SmartInsightDayInsightEntrySource {
    const activeFilters = {
      [ACTIVE_FILTERS.DAY]: stayDate,
    };
    const stringifiedActiveFilters = stringifyObject(activeFilters);
    const queryParams = {
      activeFilters: stringifiedActiveFilters,
      highlight: DETAIL_PANE_DETAIL_SECTION_CARD_IDS.DEMAND_INDICATORS,
      smartInsightsDay: stayDate,
    };
    const routeName = 'hotel.market-insight';
    const href = this.router.urlFor(routeName, this.subscriptionId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.market'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.subscriptionId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasMarketInsightAccess,
    };
  }

  private getSourceOverview(
    stayDate: string,
  ): SmartInsightDayInsightEntrySource {
    const { insightElementType } = this.info;
    const fromDate = stayDate;
    const toDate = stayDate;

    const highlightTileId =
      INSIGHT_TYPE_TO_OVERVIEW_TILE_ID[insightElementType];

    const queryParams = {
      activeFilters: stringifyObject({
        [ACTIVE_FILTERS.DAY]: stayDate,
      }),
      fromDate,
      toDate,
      rateParam: RATE_PARAMS.LOWEST,
      otaId: OTA_IDS.BOOKINGDOTCOM,
      smartInsightsDay: stayDate,
      ...(highlightTileId && { highlight: highlightTileId }),
    };

    const routeName = 'hotel.overview';
    const href = this.router.urlFor(routeName, this.subscriptionId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.overview'),
      href,
      navigateToSource: (targetBlank = false): void => {
        // Necessary for Rates, Ranking, Overview products:
        // In order to set the selected compset filter to the primary compset, we need to set the active compset in the hotelAndMarketService.
        // Otherwise the QP validation will reset the compsetId QP back to the ID of the active compset.
        this.hotelAndMarketService.setActiveCompsetById('1');

        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.subscriptionId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasOverviewDashboardTabAccess,
    };
  }

  private getSourceRateInsight(
    month: string,
  ): SmartInsightDayInsightEntrySource {
    const { insightElementType, stayDate, text } = this.info;

    const queryParams: Record<string, unknown> = {
      ...DEFAULT_QP_VALUES.hotel.rates,
      compsetId: '1',
      highlight: `smart-insight-day-${stayDate}`,
      month,
      rateParam: RATE_PARAMS.LOWEST, // Currently we always send lowest as QP to the Smart Insights API
      smartInsightsDay: stayDate,
    };

    switch (insightElementType) {
      case SMART_INSIGHT_DAY_ENTRY_TYPE.OCCUPANCY: {
        queryParams['view'] = RATES_VIEWS.TABLE;
        break;
      }

      case SMART_INSIGHT_DAY_ENTRY_TYPE.PICKUP: {
        queryParams['view'] = RATES_VIEWS.TABLE;
        if (PICKUP_SEVEN_DAYS_AGO_TEMPLATE_KEYS.has(text.templateKey)) {
          // Apply the compare filter in Rates for 7-days-ago pickup insights.
          queryParams['compareType'] = COMPARETYPES.DATE;
          queryParams['compareValue'] = this.hotelAndMarketService
            .getLocalTime()
            .startOf('day')
            .subtract(7, 'day')
            .format('YYYY-MM-DD');
        }
        break;
      }

      case SMART_INSIGHT_DAY_ENTRY_TYPE.RATES: {
        const isStlyComparison =
          TEXT_VARIABLES_RATES.REFERENCE_PRCT_STLY in text.variables;
        const isMedianCompsetRateComparison =
          TEXT_VARIABLES_RATES.OWN_VS_MEDIAN_PRCT in text.variables;

        if (isStlyComparison) {
          // STLY comparison rate is only visible on the table view, when comparing vs. SWLY.
          queryParams['view'] = RATES_VIEWS.TABLE;
          queryParams['compareType'] = COMPARETYPES.SWLY;
          queryParams['compareValue'] = true;
        } else if (isMedianCompsetRateComparison) {
          // Median compset rate is not visible on the table view, so switch to calendar or chart view. In this case we chose calendar view since it's the default.
          queryParams['view'] = RATES_VIEWS.CALENDAR;
        }

        break;
      }

      case SMART_INSIGHT_DAY_ENTRY_TYPE.PRICE_POSITION: {
        // Rate position is only visible in the calendar tooltip
        queryParams['view'] = RATES_VIEWS.CALENDAR;
        break;
      }

      default:
        // Switch to the calendar view by default.
        queryParams['view'] = RATES_VIEWS.CALENDAR;
    }

    const subscriptionToRedirectTo =
      this.unifiedHotelViewSubscriptionIds.rateiRedirectId;

    const routeName = 'hotel.rates';
    const href = this.router.urlFor(routeName, subscriptionToRedirectTo, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.rates'),
      href,
      navigateToSource: (targetBlank = false): void => {
        // Necessary for Rates, Ranking, Overview products:
        // In order to set the selected compset filter to the primary compset, we need to set the active compset in the hotelAndMarketService.
        // Otherwise the QP validation will reset the compsetId QP back to the ID of the active compset.
        this.hotelAndMarketService.setActiveCompsetById('1');

        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, subscriptionToRedirectTo, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasRatesAccess,
    };
  }

  private getSourceSpider(): SmartInsightDayInsightEntrySource {
    const { stayDate } = this.info;

    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    const startDate = dayjs(stayDate).startOf('month').format('YYYY-MM-DD');
    const endDate = dayjs(stayDate).endOf('month').format('YYYY-MM-DD');
    const queryParams = {
      pickupFrom: yesterday,
      startDate,
      endDate,
      smartInsightsDay: stayDate,
    };

    const subscriptionToRedirectTo =
      this.unifiedHotelViewSubscriptionIds.biRedirectId;

    const routeName = 'hotel.day-by-day.spider-dashboard';
    const href = this.router.urlFor(
      routeName,
      subscriptionToRedirectTo,
      DAY_BY_DAY_PATHS[0],
      {
        queryParams,
      },
    );

    return {
      productName: this.intl.t('menu.spider.day-by-day'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.spider.updateDateRange(startDate, endDate, yesterday);
          this.router.transitionTo(
            routeName,
            subscriptionToRedirectTo,
            DAY_BY_DAY_PATHS[0],
            { queryParams },
          );
        }
      },
      hasAccess: this.hasSpiderAccess,
    };
  }

  private hasSmartInsightsFeature(featureString: string): boolean {
    const featureFlags = this.smartInsightDay.meta?.featureFlags ?? [];

    return featureFlags.includes(featureString);
  }

  private isMissingValue(value: string): boolean {
    return value === '' || value === 'NaN' || value === 'null';
  }

  private processVariableCurrency(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (Number.isFinite(valueAsNumber) && value !== '') {
      const formatNumberOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...(this.config.currency && {
          style: 'currency' as const,
          currency: this.config.currency,
          currencyDisplay: 'symbol' as const,
        }),
      };

      processedVariables[key] = this.intl.formatNumber(
        valueAsNumber,
        formatNumberOptions,
      );
    } else if (this.isMissingValue(value)) {
      processedVariables[key] = this.intl.t('general.missing');
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariableDate(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const date = dayjs(value);

    if (date.isValid()) {
      processedVariables[key] = date.format('LL');
    } else if (this.isMissingValue(value)) {
      processedVariables[key] = this.intl.t('general.missing');
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariableNumber(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
    options: { signDisplay?: 'always' | 'auto' | 'exceptZero' | 'never' } = {},
  ): void {
    const valueAsNumber = Number(value);

    if (Number.isFinite(valueAsNumber) && value !== '') {
      processedVariables[key] = this.intl.formatNumber(valueAsNumber, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...options,
      });
    } else if (this.isMissingValue(value)) {
      processedVariables[key] = this.intl.t('general.missing');
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariablePercentage(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (Number.isFinite(valueAsNumber) && value !== '') {
      processedVariables[key] = this.intl.formatNumber(valueAsNumber / 100, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } else if (this.isMissingValue(value)) {
      processedVariables[key] = this.intl.t('general.missing');
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariablePercentagePoint(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (Number.isFinite(valueAsNumber) && value !== '') {
      const formattedCount = this.intl.formatNumber(valueAsNumber, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      processedVariables[key] = this.intl.t('general.percentage_point', {
        count: formattedCount,
      });
    } else if (this.isMissingValue(value)) {
      processedVariables[key] = this.intl.t('general.missing');
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariables(): Record<string, string | number> {
    const processedVariables: Record<string, string | number> = {
      // Keep classNameBold for backwards compatibility with older backend templates
      // that still use {classNameBold} for bold text formatting
      classNameBold: 'font-bold',
    };

    // Pass through all scalar variables (strings and numbers) as-is.
    // Arrays are skipped here and handled by type-specific processors.
    for (const [key, value] of Object.entries(this.info.text.variables)) {
      if (typeof value === 'string' || typeof value === 'number') {
        processedVariables[key] = value;
      }
    }

    switch (this.info.insightElementType) {
      case SMART_INSIGHT_DAY_ENTRY_TYPE.HOLIDAYS:
        this.processVariablesHolidays(processedVariables);
        break;

      case SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS:
        this.processVariablesEvents(processedVariables);
        break;

      case SMART_INSIGHT_DAY_ENTRY_TYPE.EVENTS_NEW:
        this.processVariablesEventsNew(processedVariables);
        break;

      case SMART_INSIGHT_DAY_ENTRY_TYPE.PICKUP:
        this.processVariablesPickup(processedVariables);
        break;

      case SMART_INSIGHT_DAY_ENTRY_TYPE.SELLOUT:
        this.processVariablesSellout(processedVariables);
        break;

      case SMART_INSIGHT_DAY_ENTRY_TYPE.OCCUPANCY:
        this.processVariablesOccupancy(processedVariables);
        break;

      case SMART_INSIGHT_DAY_ENTRY_TYPE.REVPAR:
        this.processVariablesRevpar(processedVariables);
        break;

      case SMART_INSIGHT_DAY_ENTRY_TYPE.ADR:
        this.processVariablesAdr(processedVariables);
        break;

      case SMART_INSIGHT_DAY_ENTRY_TYPE.PRICE_POSITION:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.RATES:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.DEMAND:
      case SMART_INSIGHT_DAY_ENTRY_TYPE.LOS_RESTRICTIONS:
        this.processVariablesRates(processedVariables);
        break;

      default:
        this.processVariablesGeneric(processedVariables);
        break;
    }

    return processedVariables;
  }

  private processVariablesAdr(
    processedVariables: Record<string, string | number>,
  ): void {
    Object.entries(this.info.text.variables).forEach(([key, value]) => {
      if (key === TEXT_VARIABLES_ADR.PER_DIFF) {
        this.processVariablePercentage(key, String(value), processedVariables);
      } else {
        this.processVariableCurrency(key, String(value), processedVariables);
      }
    });
  }

  private processVariablesEvents(
    processedVariables: Record<string, string | number>,
  ): void {
    Object.entries(this.info.text.variables).forEach(([key, value]) => {
      if (
        key === TEXT_VARIABLES_EVENTS.START_DATE ||
        key === TEXT_VARIABLES_EVENTS.END_DATE
      ) {
        this.processVariableDate(key, String(value), processedVariables);
      }
    });
  }

  processVariablesEventsNew(
    processedVariables: Record<string, string | number>,
  ): void {
    const value =
      this.info.text.variables[TEXT_VARIABLES_EVENTS_NEW.EVENT_NAMES];

    if (!value) {
      this.processVariablesGeneric(processedVariables);
      return;
    }

    const eventNames: string[] = Array.isArray(value) ? value : [];

    processedVariables[TEXT_VARIABLES_EVENTS_NEW.EVENT_NAME] =
      eventNames[0] ?? '';
    processedVariables[TEXT_VARIABLES_EVENTS_NEW.EVENT_NAME_ONE] =
      eventNames[0] ?? '';
    processedVariables[TEXT_VARIABLES_EVENTS_NEW.EVENT_NAME_TWO] =
      eventNames[1] ?? '';
  }

  private processVariablesGeneric(
    processedVariables: Record<string, string | number>,
  ): void {
    Object.entries(this.info.text.variables).forEach(([key, value]) => {
      this.processVariableCurrency(key, String(value), processedVariables);
    });
  }

  private processVariablesHolidays(
    processedVariables: Record<string, string | number>,
  ): void {
    const value =
      this.info.text.variables[TEXT_VARIABLES_HOLIDAYS.HOLIDAY_NAMES];

    if (!value) {
      this.processVariablesGeneric(processedVariables);
      return;
    }

    const holidayNames: string[] = Array.isArray(value) ? value : [];

    processedVariables[TEXT_VARIABLES_HOLIDAYS.HOLIDAY_NAME] =
      holidayNames[0] ?? '';
    processedVariables[TEXT_VARIABLES_HOLIDAYS.HOLIDAY_NAME_ONE] =
      holidayNames[0] ?? '';
    processedVariables[TEXT_VARIABLES_HOLIDAYS.HOLIDAY_NAME_TWO] =
      holidayNames[1] ?? '';
  }

  private processVariablesOccupancy(
    processedVariables: Record<string, string | number>,
  ): void {
    Object.entries(this.info.text.variables).forEach(([key, value]) => {
      if (
        [
          TEXT_VARIABLES_OCCUPANCY.VALUE,
          TEXT_VARIABLES_OCCUPANCY.VALUE_TRANSIENT,
          TEXT_VARIABLES_OCCUPANCY.VALUE_GROUP,
          TEXT_VARIABLES_OCCUPANCY.VALUE_MARKET_REF,
        ].includes(key)
      ) {
        this.processVariablePercentage(key, String(value), processedVariables);
      } else if (key === TEXT_VARIABLES_OCCUPANCY.VALUE_MARKET_REF_DIFF) {
        this.processVariablePercentagePoint(
          key,
          String(value),
          processedVariables,
        );
      } else {
        this.processVariableCurrency(key, String(value), processedVariables);
      }
    });
  }

  private processVariablesPickup(
    processedVariables: Record<string, string | number>,
  ): void {
    Object.entries(this.info.text.variables).forEach(([key, value]) => {
      if (key === TEXT_VARIABLES_PICKUP.REF) {
        this.processVariableNumber(key, String(value), processedVariables, {
          signDisplay: 'always',
        });
      } else {
        this.processVariableCurrency(key, String(value), processedVariables);
      }
    });
  }

  private processVariablesRates(
    processedVariables: Record<string, string | number>,
  ): void {
    Object.entries(this.info.text.variables).forEach(([key, value]) => {
      if (
        [
          TEXT_VARIABLES_RATES.OWN_VS_MEDIAN_PRCT,
          TEXT_VARIABLES_RATES.OWN_VS_COMP_PRCT,
          TEXT_VARIABLES_RATES.REFERENCE_PRCT_STLY,
        ].includes(key)
      ) {
        this.processVariablePercentage(key, String(value), processedVariables);
      } else if (
        key === TEXT_VARIABLES_RATES.NB_ANOMALOUS_COMPETITORS ||
        key === TEXT_VARIABLES_RATES.TOTAL_COMPETITOR_ANOMALIES ||
        key === TEXT_VARIABLES_RATES.NB_OTHER_COMPETITORS
      ) {
        this.processVariableNumber(key, String(value), processedVariables);
      } else {
        this.processVariableCurrency(key, String(value), processedVariables);
      }
    });
  }

  private processVariablesRevpar(
    processedVariables: Record<string, string | number>,
  ): void {
    Object.entries(this.info.text.variables).forEach(([key, value]) => {
      if (key === TEXT_VARIABLES_REVPAR.PER_DIFF) {
        this.processVariablePercentage(key, String(value), processedVariables);
      } else {
        this.processVariableCurrency(key, String(value), processedVariables);
      }
    });
  }

  private processVariablesSellout(
    processedVariables: Record<string, string | number>,
  ): void {
    Object.entries(this.info.text.variables).forEach(([key, value]) => {
      if (
        key === TEXT_VARIABLES_SELLOUT.CHANNEL_NAME ||
        key === TEXT_VARIABLES_SELLOUT.REF_CHANNEL_NAME
      ) {
        processedVariables[key] =
          CHANNEL_NAMES_MAPPING[
            String(value) as keyof typeof CHANNEL_NAMES_MAPPING
          ] || String(value);
      } else {
        this.processVariableCurrency(key, String(value), processedVariables);
      }
    });
  }
}
